import { useRef, useCallback, useEffect } from 'react';

type Callback<T> = (event: T) => void;

/**
 * A simple mimic of the EventEmitter type that node has, but in Hook form.
 *
 * Allows subscription of emitted events.
 */
export function useEventEmitter<T>() {
  const listenersRef = useRef<Callback<T>[]>([]);
  const events = useRef<T[]>([]);

  const emit = useCallback((event: T) => {
    events.current.push(event);
    for (const listener of listenersRef.current) {
      listener(event);
    }
  }, []);

  const subscribe = useCallback((callback: Callback<T>, initial?: Callback<T[]>) => {
    const index = listenersRef.current.length;
    initial?.(events.current);
    listenersRef.current.push(callback);
    return () => {
      listenersRef.current.splice(index, 1);
    };
  }, []);

  useEffect(() => {
    return () => {
      events.current = [];
      listenersRef.current = [];
    }
  }, []);

  return { emit, subscribe };
}
